import { Injectable } from '@angular/core';
import { SystemPropertiesService } from '@app/core/services/system-properties.service';
import { UserProfileService } from '@app/modules/user-profile/services/user-profile.service';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { SystemPropertyKey } from '@common/enums/SystemPropertyKey';
import { LocalStorageKey } from '@app/core/enums/local-storage-key.enum';
import { IFundFlowDiagram } from '@common/models/ISystemProperty';

@Injectable({
  providedIn: 'root'
})
export class FeatureFlagService {
  private flags: Record<string, boolean> = {};
  private readonly _featureFlag$: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  
  constructor(private readonly userProfileService: UserProfileService,
    private readonly systemPropertyService: SystemPropertiesService,
  ) { }

  public setFeatureFlag(value: any) {
    this._featureFlag$.next(value);
  }
  public getFeatureFlag() {
    return this._featureFlag$.asObservable();
  }

  public async loadFlags(): Promise<void> {
    const systemPropValue = await this.getFeatureFlags();

    this.flags = systemPropValue?.reduce((acc, flag) => {
      acc[flag.feature] = {
        enabled: flag.enabled,
        ...(flag.roles ? { roles: flag.roles } : {})
      };
      return acc;
    }, {} as Record<string, { enabled: boolean; roles?: string[] }>);

    this.setFeatureFlag(this.flags);
  }

  async getFeatureFlags(): Promise<any> {
    const systemData = JSON.parse(localStorage[LocalStorageKey.systemPropertyCache] || '[]');
    let flagFeatures = systemData?.find(r => r.key === SystemPropertyKey.featureFlags) || null;

    if (!flagFeatures) {
      const res: any[] = await firstValueFrom(this.systemPropertyService.getAll());
      localStorage[LocalStorageKey.systemPropertyCache] = JSON.stringify(res);
      flagFeatures = res.find(r => r.key === SystemPropertyKey.featureFlags) || null;
    }

    return flagFeatures?.value || [];
  }


  async isEnabled(key: string):  Promise<boolean> {
     const res = await firstValueFrom(this.userProfileService.getMe());
     const userRoles = res?.roles || [];
     const feature : any = this.flags[key] || null;
      if (!feature?.enabled) {
        return false;
      }
      if (feature.roles && feature.roles.length > 0) {
       return feature.roles.some(role => userRoles.includes(role));
      }

      // No role check needed
      return true;
  }

  async getFundFlowDiagram(key: string): Promise<IFundFlowDiagram> {
    const defaultValue: IFundFlowDiagram = { enabled: false, roles: [] };
    try {
        const systemData = JSON.parse(localStorage.getItem(LocalStorageKey.systemPropertyCache) ?? '[]');
        systemData.find((item: any) => item.key === key)?.value ?? defaultValue
        return systemData.find((item: any) => item.key === key)?.value ?? defaultValue;
    } catch {
        return defaultValue;
    }
  }

  async isEnabledFundFlowDiagram():  Promise<boolean> {
    const res = await firstValueFrom(this.userProfileService.getMe());
    const userRoles = res?.roles || [];
    const feature : any = await this.getFundFlowDiagram(SystemPropertyKey.fundReport);
    if (!feature?.enabled) {
      return false;
    }
    if (feature.roles && feature.roles.length > 0) {
      return feature.roles.some(role => userRoles.includes(role));
    }

    // No role check needed
    return true;
  }
}
